import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Skeleton } from 'antd';

// REDUX
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { clearNotifications } from 'store/modules/app/notifications/actions';
import {
	consultarVeiculoV110Clear,
	consultarVeiculoV110Request,
} from 'store/modules/api/detranCrv/SolicitarDocumentos/ConsultarVeiculoV110/actions';
import {
	consultarRestricaoVeiculoRequest,
	consultarRestricaoVeiculoClear,
} from 'store/modules/api/detranCrv/ConsultarRestricoes/consultarRestricaoVeiculo/actions';
import { IRequestConsultaRestricaoVeiculo } from 'store/modules/api/detranCrv/ConsultarRestricoes/consultarRestricaoVeiculo/types';

// COMPONENTS
import Section from 'components/Common/Section';
import ButtonVoltar from 'components/Common/ButtonVoltar';
import Error from 'components/Common/Notifications/FormError/index';
import PesquisaPadraoCrv from 'pages/DetranCrv/components/pages/PesquisaPadraoCrv';

// ROUTES
import { ROUTE_DETRAN_CRV } from 'routes/paths';

// FORM
import { Formik, FormikProps } from 'formik';

// UTILS
import getValidationsErrors from 'utils/getValidationsErrors';
import { errorCodesAllowedsConsultarRestricaoVeiculo } from 'pages/DetranCrv/utils/constants/servicesResponse';
import { ConsultarTaxaServicoRequest } from 'store/modules/api/detranCrv/ConsultarRestricoes/consultarTaxaServico/types';
import {
	consultarTaxaServicoClear,
	consultarTaxaServicoRequest,
} from 'store/modules/api/detranCrv/ConsultarRestricoes/consultarTaxaServico/actions';
import { initialValues, ITransferenciaProprietario, schema } from './form';

const TransferenciaProprietario: React.FC = () => {
	const dispatch = useDispatch();
	const history = useHistory();

	const [bodyRestricao, setBodyRestricao] =
		useState<IRequestConsultaRestricaoVeiculo>({
			placa: '',
			documento: '',
			espelho: '',
		});

	const [notificationErrors, setNotificationErrors] = useState<string[]>([]);
	const [valuesState, setValuesState] = useState(initialValues);
	const [categoriaOficial, setCategoriaOficial] = useState<string | number>('');

	const { consultarVeiculoV110 } = useSelector(
		(state: ApplicationState) => state.api.detranCrv.solicitarDocumentos,
	);

	const { consultarRestricaoVeiculo, consultarTaxaServico } = useSelector(
		(state: ApplicationState) => state.api.detranCrv.consultarRestricoes,
	);

	useEffect(() => {
		if (
			consultarVeiculoV110.status === 200 &&
			consultarVeiculoV110.data &&
			consultarTaxaServico.status === 0 &&
			categoriaOficial !== 3
		) {
			const dataConsultarTaxaServico: ConsultarTaxaServicoRequest = {
				codigoTaxaServico: '2',
				identificadorTaxa:
					consultarVeiculoV110.data.caracteristicaVeiculo.renavam.toString() ||
					'',
				despachante: '',
			};

			dispatch(consultarTaxaServicoRequest(dataConsultarTaxaServico));
		}
	}, [
		valuesState,
		dispatch,
		consultarTaxaServico.status,
		consultarVeiculoV110.status,
		consultarVeiculoV110.data,
		categoriaOficial,
	]);

	useEffect(() => {
		let bodyRestricaoReq: IRequestConsultaRestricaoVeiculo = {
			placa: '',
			documento: '',
			espelho: '',
		};
		if (
			consultarVeiculoV110.status === 200 &&
			(consultarTaxaServico.status === 200 || categoriaOficial === 3)
		) {
			if (
				valuesState.restricaoFinanceira !==
				consultarVeiculoV110.data?.caracteristicaVeiculo.gravame.restricao
			) {
				setNotificationErrors([
					'ERRO NA CONSISTÊNCIA DE GRAVAMES PARA ESTE VEÍCULO. FAVOR VERIFICAR.',
				]);
			} else {
				bodyRestricaoReq = {
					placa: consultarVeiculoV110.data?.caracteristicaVeiculo.placa || '',
					documento:
						valuesState.documento ||
						consultarVeiculoV110.data?.caracteristicaVeiculo.proprietario.cpfCnpjProprietario.toString() ||
						'',
					espelho: valuesState.espelho,
					renavam:
						consultarVeiculoV110.data?.caracteristicaVeiculo.renavam.toString(),
					uf: consultarVeiculoV110.data?.caracteristicaVeiculo.ufPlacaAnterior,
				};
				if (valuesState.documento && consultarRestricaoVeiculo.status === 0) {
					setBodyRestricao(bodyRestricaoReq);
					dispatch(
						consultarRestricaoVeiculoRequest(
							bodyRestricaoReq as IRequestConsultaRestricaoVeiculo,
						),
					);
				}

				if (
					consultarRestricaoVeiculo.status === 200 ||
					(consultarRestricaoVeiculo.status ===
						errorCodesAllowedsConsultarRestricaoVeiculo.code199
							.statusResponse &&
						consultarRestricaoVeiculo.dataError?.codigo ===
							errorCodesAllowedsConsultarRestricaoVeiculo.code199.code)
				) {
					dispatch(clearNotifications());
					history.push({
						pathname: '/detran-crv/transferencia-proprietario/confirmacao',
						state: {
							espelho: bodyRestricao.espelho,
							cpf: bodyRestricao.documento || '',
						},
					});
				}
			}
		}
	}, [
		consultarVeiculoV110,
		bodyRestricao,
		history,
		dispatch,
		valuesState,
		consultarRestricaoVeiculo.status,
		consultarRestricaoVeiculo.dataError,
		consultarTaxaServico.status,
		categoriaOficial,
	]);

	const handleClear = useCallback(
		(formik?: FormikProps<ITransferenciaProprietario>) => {
			if (formik) {
				formik.handleReset();
			}
			dispatch(clearNotifications());
			setNotificationErrors([]);
			dispatch(consultarVeiculoV110Clear());
			dispatch(consultarRestricaoVeiculoClear());
			dispatch(consultarTaxaServicoClear());
		},
		[dispatch],
	);

	const handleSubmit = useCallback(
		(values: ITransferenciaProprietario) => {
			handleClear();
			setValuesState(values);
			const body = {
				placa: values.placa,
				chassi: values.chassi,
				tipoConsulta: values.tipoConsulta,
				restricaoFinanceira: values.restricaoFinanceira,
				documento: values.documento,
			};
			dispatch(consultarVeiculoV110Request(body));
		},
		[dispatch, handleClear],
	);

	return (
		<Suspense fallback={<Skeleton paragraph={{ rows: 4 }} />}>
			{notificationErrors.length > 0 && (
				<Error
					errors={notificationErrors}
					onClose={() => setNotificationErrors([])}
				/>
			)}
			<Section title="Pesquisar">
				<Formik
					validateOnChange={false}
					validateOnBlur={false}
					initialValues={initialValues}
					validate={values => getValidationsErrors(values, schema)}
					onSubmit={handleSubmit}
				>
					{(formik: FormikProps<any>) => {
						return (
							<PesquisaPadraoCrv
								formik={formik}
								setNotificationErrors={setNotificationErrors}
								notificationErrors={notificationErrors}
								setCategoriaOficial={setCategoriaOficial}
							/>
						);
					}}
				</Formik>
			</Section>
			<ButtonVoltar route={ROUTE_DETRAN_CRV} />
		</Suspense>
	);
};

export default TransferenciaProprietario;
